import { useState, useEffect, useCallback } from 'react';
import { orderService, Order, OrderFilters, OrderStats } from '../services/order.service';
import { PAGINATION } from '@erpgold/shared';

interface UseOrdersReturn {
  orders: Order[];
  loading: boolean;
  error: Error | null;
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: OrderFilters;
  setFilters: (filters: OrderFilters) => void;
  refetch: () => void;
}

export const useOrders = (initialFilters: OrderFilters = {}): UseOrdersReturn => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<OrderFilters>({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    ...initialFilters,
  });
  const [meta, setMeta] = useState({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    total: 0,
    totalPages: 0,
  });

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await orderService.getOrders(filters);
      setOrders(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch orders'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return {
    orders,
    loading,
    error,
    meta,
    filters,
    setFilters,
    refetch: fetchOrders,
  };
};

interface UseOrderDetailReturn {
  order: Order | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  cancelOrder: (reason?: string) => Promise<void>;
}

export const useOrderDetail = (id: string): UseOrderDetailReturn => {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await orderService.getOrderById(id);
      setOrder(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch order'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const cancelOrder = async (reason?: string): Promise<void> => {
    if (!id) return;
    await orderService.cancelOrder(id, reason);
    setOrder((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        status: 'cancelled',
        updatedAt: new Date().toISOString(),
        timeline: [
          ...(prev.timeline || []),
          {
            status: 'cancelled',
            timestamp: new Date().toISOString(),
            note: reason || 'Cancelled by platform admin',
          },
        ],
      };
    });
  };

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  return {
    order,
    loading,
    error,
    refetch: fetchOrder,
    cancelOrder,
  };
};

interface UseOrderStatsReturn {
  stats: OrderStats | null;
  loading: boolean;
  error: Error | null;
}

export const useOrderStats = (): UseOrderStatsReturn => {
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await orderService.getOrderStats();
        setStats(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch order stats'));
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  return { stats, loading, error };
};
